import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { normalizeNumber } from '../../../../services/numberUtilis';
import LoggedUserContext from '../../../../contextProviders/LoggedUserContext';
import User from '../../../../../../../domain/models/user';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import ProductVariationsProp from '../../propTypes/productVariationsProp';
import translate from '../../../../commonComponents/translate';

function CommissionableItem({ label, value }) {
  return (
    <div className="container">
      <p className="label">{label}:</p>
      <p className="value">{normalizeNumber(value, 2)}</p>
    </div>
  );
}

CommissionableItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
};

function CartProductInfo(props) {
  const { t, product, productsAvailability, getStockManagementMsg } = props;
  const { loggedUser } = useContext(LoggedUserContext);
  const { tenant } = useContext(ConfigsContext);
  const { directSelling: isDirectSellingEnabled, directSellingSettings = {} } =
    tenant?.settings || {};
  const { CVIntegration: hasCVIntegration, VAIntegration: hasVAIntegration } =
    directSellingSettings;

  const { id, va, commissionable, cv, amount } = product || {};
  const isUserCollaborator = loggedUser?.type === User.TYPE.COLLABORATOR;

  const showVAInformation = useMemo(
    () =>
      va &&
      commissionable &&
      isUserCollaborator &&
      hasVAIntegration &&
      isDirectSellingEnabled,
    [va, commissionable, isUserCollaborator, tenant],
  );

  const showCVInformation = useMemo(
    () =>
      cv &&
      commissionable &&
      isUserCollaborator &&
      hasCVIntegration &&
      isDirectSellingEnabled,
    [cv, commissionable, isUserCollaborator, tenant],
  );

  const checkVariationAvailability = useCallback(
    (variationId, productsToCheck) => {
      const isNotAvailable = productsToCheck.find(prod => prod === variationId);
      if (isNotAvailable) {
        return (
          <span className="warning-red">
            {t('shop.checkout.availability.products.variation')}
          </span>
        );
      }
      return <span />;
    },
    [],
  );

  return (
    <>
      <div className="stock-wrapper">
        {productsAvailability?.length > 0 &&
          checkVariationAvailability(id, productsAvailability)}
        {getStockManagementMsg(product)}
      </div>
      <div className="commissionable-values-wrapper">
        {showVAInformation ? (
          <CommissionableItem label={t('commissions.va')} value={va * amount} />
        ) : null}
        {showCVInformation ? (
          <CommissionableItem label={t('commissions.cv')} value={cv * amount} />
        ) : null}
      </div>
    </>
  );
}

CartProductInfo.propTypes = {
  t: PropTypes.func.isRequired,
  getStockManagementMsg: PropTypes.func.isRequired,
  product: PropTypes.shape(ProductVariationsProp.propType).isRequired,
  productsAvailability: PropTypes.arrayOf(PropTypes.string),
};

CartProductInfo.defaultProps = {
  productsAvailability: [],
};

export default translate(CartProductInfo);
